import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Divider,
  Alert,
  Box,
  CircularProgress,
  IconButton,
  TextField,
  Button,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import RadioButtonUncheckedOutlinedIcon from '@mui/icons-material/RadioButtonUncheckedOutlined';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import PriorityHighOutlinedIcon from '@mui/icons-material/PriorityHighOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CustomButton from '../../components/buttons/CustomButton';
import { DataProviderInstance } from '../../api/DataProvider';
import { buildFiscalFilename, GetFiscalPublicPdfUrl, describeFiscalAutoSend } from '../../api/FiscalAPI';
import { GetCompanyAutomationSettingsAPI, UpdateCompanyAutomationSettingAPI } from '../../api/API';
import { useRoleAccess } from '../../hooks/useRoleAccess';
import { USER_ROLES } from '../../constants/userRoles';
import { getAccessToken } from '../../api/authTokens';
import { SendWhatsAppMessage } from '../../api/WhatsappAPI';
import MessagesTemplate from '../../consts/MessagesTemplate';
import FiscalInvoiceHistory from './FiscalInvoiceHistory';
import FiscalInvoiceReviewModal from './FiscalInvoiceReviewModal';
import styles from './FiscalInvoicePopup.module.css';
import useSubscriptionFeatures from '../../hooks/useSubscriptionFeatures';
import { SUBSCRIPTION_PERMISSIONS } from '../../consts/SubscriptionFeatures';
import FeatureLockBanner from '../../components/subscription/FeatureLockBanner';
import PlanUpgradeModal from '../../components/subscription/PlanUpgradeModal';
import { procedureLabels } from '../../pages/appointments/v2/utils/appointmentUtils';
import { PROFESSIONAL_PLAN_NAME } from '../../components/subscription/planLockTexts';

// Automação de envio automático da nota (mesma chave de Preferências > Automação)
const FISCAL_AUTO_SEND_TYPE = 'fiscal_invoice_send';

/**
 * Status da nota fiscal e suas configurações visuais
 */
const STATUS_CONFIG = {
  pending: {
    label: 'Não emitida',
    icon: <RadioButtonUncheckedOutlinedIcon fontSize="inherit" />,
    badgeClass: 'statusBadge--pending',
    description: 'A nota fiscal ainda não foi emitida para este atendimento.',
  },
  processing: {
    label: 'Em processamento',
    icon: <AutorenewOutlinedIcon fontSize="inherit" />,
    badgeClass: 'statusBadge--processing',
    description: 'A nota fiscal está sendo processada pela prefeitura.',
  },
  queued: {
    label: 'Na fila',
    icon: <AutorenewOutlinedIcon fontSize="inherit" />,
    badgeClass: 'statusBadge--queued',
    description: 'A nota fiscal está na fila de processamento.',
  },
  issued: {
    label: 'Emitida',
    icon: <CheckOutlinedIcon fontSize="inherit" />,
    badgeClass: 'statusBadge--issued',
    description: 'Nota fiscal emitida com sucesso.',
  },
  error: {
    label: 'Erro',
    icon: <PriorityHighOutlinedIcon fontSize="inherit" />,
    badgeClass: 'statusBadge--error',
    description: 'Ocorreu um erro ao emitir a nota fiscal.',
  },
  canceled: {
    label: 'Cancelada',
    icon: <CloseOutlinedIcon fontSize="inherit" />,
    badgeClass: 'statusBadge--canceled',
    description: 'A nota fiscal foi cancelada.',
  },
};

/**
 * Resolve variáveis de template na descrição do serviço.
 * Variáveis suportadas:
 *   {procedure_label}   — nome do procedimento
 *   {patient_name}      — nome do paciente
 *   {appointment_date}  — data do agendamento (dd/mm/aaaa)
 */
function resolveDescriptionTemplate(template, { procedureLabel, patientName, appointmentDate }) {
  if (!template) return '';
  return template
    .replace(/\{procedure_label\}/gi, procedureLabel || '')
    .replace(/\{patient_name\}/gi, patientName || '')
    .replace(/\{appointment_date\}/gi, appointmentDate || '');
}

/**
 * Formata uma data ISO para exibição no formato dd/mm/aaaa HH:MM
 */
function formatDateTime(isoString) {
  if (!isoString) return null;
  try {
    const d = new Date(isoString.replace(' ', 'T'));
    const day   = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year  = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const mins  = String(d.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${mins}`;
  } catch {
    return isoString;
  }
}

/**
 * FiscalInvoicePopup
 *
 * Props:
 *   appointmentData  — objeto do agendamento (id, patient, price, procedure, invoiceId, date)
 *   patientData      — objeto do paciente (name, cpf, email)
 *   onHide           — callback ao fechar
 */
function FiscalInvoicePopup({ appointmentData, patientData, onHide }) {
  const dataProvider = DataProviderInstance();
  const { GetResourceById, CompanyConfig } = DataProviderInstance();
  const { hasRole } = useRoleAccess();
  const canEditAutoSend = hasRole([USER_ROLES.COMPANY_OWNER, USER_ROLES.COMPANY_ADMIN]);

  // Verificação de permissão de assinatura
  const { hasPermission } = useSubscriptionFeatures();
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const hasNfsePermission = hasPermission(SUBSCRIPTION_PERMISSIONS.FOCUS_NFSE_INTEGRATION);

  // Histórico completo de notas do atendimento (array)
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoice, setLoadingInvoice] = useState(true);

  // Estado do formulário de emissão
  const [serviceDescription, setServiceDescription] = useState('');
  const [issuing, setIssuing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Estado do modal de revisão
  const [showReviewModal, setShowReviewModal] = useState(false);

  // Estado de cancelamento
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelJustificativa, setCancelJustificativa] = useState('');
  const [canceling, setCanceling] = useState(false);

  // Mensagens
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [downloadErrorMsg, setDownloadErrorMsg] = useState('');

  // Configurações fiscais da empresa
  const [fiscalSettings, setFiscalSettings] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(true);

  // Preferência da clínica: enviar a nota ao paciente por WhatsApp assim que ficar pronta
  const [autoSend, setAutoSend] = useState({ loaded: false, enabled: false, blocked: false, blockedMessage: '' });
  const [autoSendSaving, setAutoSendSaving] = useState(false);
  const autoSendPollRef = React.useRef(0);

  // ─── Polling de disponibilidade do PDF ──────────────────────────────────────
  const pollingRef = React.useRef(null);
  const pollingAttemptsRef = React.useRef(0);
  const MAX_POLLING_ATTEMPTS = 10;
  const POLLING_INTERVAL_MS = 3000;

  // ─── Carregamento inicial ────────────────────────────────────────────────────

  useEffect(() => {
    loadFiscalSettings();
    loadExistingInvoice();
  }, []);

  const loadFiscalSettings = () => {
    setLoadingSettings(true);
    dataProvider.GetFiscalSettings((result) => {
      setLoadingSettings(false);
      if (result?.success && result.settings) {
        setFiscalSettings(result.settings);

        // Resolver template da descrição com dados dinâmicos
        const procedureLabel = procedureLabels(appointmentData, CompanyConfig?.Procedures);
        const patientName = patientData?.name || '';
        const rawDate = appointmentData?.date || '';
        // Formatar data: "YYYY-MM-DD HH:MM:SS" → "dd/mm/aaaa"
        const appointmentDate = rawDate
          ? rawDate.split(' ')[0].split('-').reverse().join('/')
          : '';

        const resolved = resolveDescriptionTemplate(
          result.settings.default_service_description || '',
          { procedureLabel, patientName, appointmentDate }
        );
        setServiceDescription(resolved);
      }
    });
  };

  const loadExistingInvoice = () => {
    setLoadingInvoice(true);
    dataProvider.GetFiscalInvoiceHistory(appointmentData?.id, (result) => {
      setLoadingInvoice(false);
      if (result?.success && Array.isArray(result.invoices)) {
        setInvoices(result.invoices);
      } else {
        setInvoices([]);
      }
    });
  };

  // ─── Nota ativa (derivada do array de invoices) ──────────────────────────────
  // Definida aqui para ser usada no useEffect de polling abaixo
  const activeInvoice = invoices.find(inv =>
    ['queued', 'processing', 'issued'].includes(inv.status)
  ) ?? null;

  // Disponibilidade de PDF e XML derivada dos campos retornados pelo backend
  const pdfAvailable = activeInvoice?.has_pdf === true;
  const xmlAvailable = activeInvoice?.has_xml === true;

  // ─── Polling automático quando status é 'issued' mas PDF ainda não está disponível ──
  useEffect(() => {
    const shouldPoll = activeInvoice?.status === 'issued' && !activeInvoice?.has_pdf;

    if (shouldPoll && pollingAttemptsRef.current < MAX_POLLING_ATTEMPTS) {
      pollingRef.current = setInterval(() => {
        pollingAttemptsRef.current += 1;

        if (pollingAttemptsRef.current >= MAX_POLLING_ATTEMPTS) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          return;
        }

        loadExistingInvoice();
      }, POLLING_INTERVAL_MS);
    } else {
      // PDF disponível ou não precisa de polling — limpar
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      pollingAttemptsRef.current = 0;
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [activeInvoice?.status, activeInvoice?.has_pdf]); // eslint-disable-line react-hooks/exhaustive-deps

  // Carrega a preferência de envio automático (e se o plano/número permite)
  useEffect(() => {
    if (CompanyConfig?.Id === undefined || CompanyConfig?.Id === null) return;
    GetCompanyAutomationSettingsAPI(CompanyConfig.Id, (response) => {
      const setting = (response?.settings || []).find(item => item.automation_type === FISCAL_AUTO_SEND_TYPE);
      const planRestriction = response?.plan_restricted_types?.[FISCAL_AUTO_SEND_TYPE];
      const trialBlocked = Boolean(response?.trial_restricted)
        && !(response?.trial_allowed_types || []).includes(FISCAL_AUTO_SEND_TYPE);
      setAutoSend({
        loaded: true,
        enabled: Boolean(Number(setting?.is_enabled)),
        blocked: Boolean(planRestriction) || trialBlocked,
        blockedMessage: planRestriction?.message || (trialBlocked ? 'Disponível ao assinar um plano' : ''),
      });
    });
  }, [CompanyConfig?.Id]);

  // Envio automático na fila: recarrega algumas vezes para mostrar "enviada" sem fechar a popup
  useEffect(() => {
    if (activeInvoice?.auto_send?.status !== 'queued' || autoSendPollRef.current >= 5) return undefined;
    const timer = setTimeout(() => {
      autoSendPollRef.current += 1;
      loadExistingInvoice();
    }, 4000);
    return () => clearTimeout(timer);
  }, [activeInvoice?.auto_send?.status, activeInvoice?.auto_send?.at]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAutoSendToggle = () => {
    const next = !autoSend.enabled;
    setAutoSendSaving(true);
    setErrorMsg('');
    UpdateCompanyAutomationSettingAPI(CompanyConfig.Id, FISCAL_AUTO_SEND_TYPE, next, (response) => {
      setAutoSendSaving(false);
      if (response?.success) {
        setAutoSend(prev => ({ ...prev, enabled: next }));
      } else {
        setErrorMsg(response?.message || 'Não foi possível salvar a preferência de envio automático.');
      }
    });
  };

  // ─── Ações ───────────────────────────────────────────────────────────────────

  const handleSendWhatsApp = () => {
    const phone = patientData?.phone;
    if (!phone) return;

    const publicPdfUrl    = activeInvoice?.public_key
      ? GetFiscalPublicPdfUrl(activeInvoice.public_key)
      : null;

    // Resolver nome do recurso (profissional) e procedimento
    const resourceLabel   = GetResourceById(appointmentData?.resource)?.Label || '';
    const procedureLabel  = procedureLabels(appointmentData, CompanyConfig?.Procedures);

    // Formatar data do agendamento: "YYYY-MM-DD HH:MM:SS" → "dd/mm/aaaa"
    const rawDate         = appointmentData?.date || '';
    const appointmentDate = rawDate
      ? rawDate.split(' ')[0].split('-').reverse().join('/')
      : '';

    // Montar contexto do atendimento (ex: "com Dr. João, Fisioterapia, no dia 04/05/2026")
    const contextParts = [];
    if (resourceLabel)   contextParts.push(`com ${resourceLabel}`);
    if (procedureLabel)  contextParts.push(procedureLabel);
    if (appointmentDate) contextParts.push(`no dia ${appointmentDate}`);
    const context = contextParts.length > 0 ? ` ${contextParts.join(', ')}` : '';

    let message = `Olá! Segue sua nota fiscal referente ao atendimento realizado${context}.`;
    if (publicPdfUrl) {
      message += `\n\n${publicPdfUrl}`;
    }

    const encodedMessage = MessagesTemplate.ConvertToWhatsAppText(message);
    // DDI do paciente — sem ele, DDD 33 (por exemplo) é lido como +33 (França)
    SendWhatsAppMessage(phone, encodedMessage, patientData?.countryCode);
  };

  // Abre o modal de revisão (com validações básicas antes)
  const handleIssueClick = () => {
    setErrorMsg('');
    setSuccessMsg('');

    // Verificação de permissão de assinatura (camada 1)
    if (!hasNfsePermission) {
      setUpgradeModalOpen(true);
      return;
    }

    // Validações básicas antes de abrir o modal
    if (!fiscalSettings?.enabled) {
      setErrorMsg('O módulo de Nota Fiscal está desabilitado. Acesse Empresa > Nota Fiscal (NFS-e) para configurar.');
      return;
    }
    if (!patientData?.cpf && !patientData?.cnpj) {
      setErrorMsg('O paciente não possui CPF/CNPJ cadastrado. Atualize o cadastro do paciente antes de emitir a nota.');
      return;
    }
    if (!appointmentData?.invoiceId || appointmentData.invoiceId === '0') {
      setErrorMsg('Este atendimento ainda não foi faturado. Fature o atendimento antes de emitir a nota fiscal.');
      return;
    }

    // Abre o modal de revisão em vez de emitir diretamente
    setShowReviewModal(true);
  };

  // Chamado pelo modal de revisão após confirmação com os dados editados
  const handleIssue = (reviewData) => {
    setIssuing(true);
    dataProvider.CreateFiscalInvoice(
      {
        appointmentId: appointmentData.id,
        paymentId: appointmentData.invoiceId,
        serviceDescription: reviewData?.serviceDescription || serviceDescription,
        sendEmail: reviewData?.sendEmail ?? false,
        tomador: reviewData?.tomador || undefined,
        amount: reviewData?.amount || undefined,
        serviceCode: reviewData?.serviceCode || undefined,
      },
      (result) => {
        setIssuing(false);
        setShowReviewModal(false);
        if (result?.success) {
          setSuccessMsg('Nota fiscal enviada para processamento!');
          loadExistingInvoice();
        } else {
          const errObj = result?.error;
          const msg = typeof errObj === 'object' ? errObj.message : (errObj || 'Erro ao emitir nota fiscal. Tente novamente.');
          setErrorMsg(msg);
        }
      }
    );
  };

  const handleRefresh = () => {
    if (!activeInvoice?.id) return;
    setRefreshing(true);
    setErrorMsg('');
    dataProvider.RefreshFiscalInvoiceStatus(activeInvoice.id, (result) => {
      setRefreshing(false);
      if (result?.success) {
        loadExistingInvoice();
      } else {
        setErrorMsg(result?.error || 'Erro ao atualizar status.');
      }
    });
  };

  const handleDownload = (type) => {
    if (!activeInvoice?.id) return;
    setDownloadErrorMsg('');
    const url      = dataProvider.GetFiscalDownloadUrl(activeInvoice.id, type);
    const token    = getAccessToken();
    const filename = buildFiscalFilename(activeInvoice, type);
    fetch(url, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then(res => {
        if (!res.ok) throw new Error('Arquivo não disponível. Verifique se a nota foi autorizada e tente novamente.');
        return res.blob();
      })
      .then(blob => {
        const blobUrl = window.URL.createObjectURL(blob);
        const link    = document.createElement('a');
        link.href     = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch(err => {
        setDownloadErrorMsg(err.message || `Erro ao baixar o arquivo ${type.toUpperCase()}. Tente novamente.`);
      });
  };

  const handleCancelRequest = () => {
    setShowCancelConfirm(true);
    setCancelJustificativa('');
    setErrorMsg('');
    setSuccessMsg('');
  };

  const handleCancelConfirm = () => {
    if (cancelJustificativa.trim().length < 15) {
      setErrorMsg('A justificativa deve ter pelo menos 15 caracteres.');
      return;
    }
    setCanceling(true);
    setErrorMsg('');
    dataProvider.CancelFiscalInvoice(
      { invoiceId: activeInvoice.id, justificativa: cancelJustificativa.trim() },
      (result) => {
        setCanceling(false);
        if (result?.success) {
          setShowCancelConfirm(false);
          setSuccessMsg('Nota fiscal cancelada com sucesso.');
          loadExistingInvoice();
        } else {
          const errObj = result?.error;
          const msg = typeof errObj === 'object' ? errObj.message : (errObj || 'Erro ao cancelar a nota fiscal.');
          setErrorMsg(msg);
        }
      }
    );
  };

  const handleCancelAbort = () => {
    setShowCancelConfirm(false);
    setCancelJustificativa('');
    setErrorMsg('');
  };

  // ─── Renderização ────────────────────────────────────────────────────────────

  const isLoading = loadingInvoice || loadingSettings;

  // currentStatus reflete a nota ativa; se não há nota ativa, exibe 'pending'
  const currentStatus = activeInvoice?.status || 'pending';
  const statusCfg = STATUS_CONFIG[currentStatus] || STATUS_CONFIG.pending;

  // canIssue: permite emitir quando não há nota ativa (canceled/error não bloqueiam)
  // Sem permissão de assinatura, ainda mostra o botão mas ele abre o modal de upgrade
  const canIssue =
    !activeInvoice &&
    (hasNfsePermission ? fiscalSettings?.enabled : true) &&
    appointmentData?.invoiceId &&
    appointmentData.invoiceId !== '0';

  const renderPatientWarnings = () => {
    const warnings = [];
    if (!patientData?.cpf && !patientData?.cnpj) {
      warnings.push('Paciente sem CPF/CNPJ cadastrado — necessário para emissão da NFS-e.');
    }
    if (!appointmentData?.invoiceId || appointmentData.invoiceId === '0') {
      warnings.push('Atendimento ainda não faturado — fature antes de emitir a nota fiscal.');
    }
    if (!fiscalSettings?.enabled) {
      warnings.push('Módulo de Nota Fiscal desabilitado. Configure em Empresa > Nota Fiscal (NFS-e).');
    }
    return warnings;
  };

  const warnings = renderPatientWarnings();

  const renderStatusBadge = () => (
    <span className={`${styles.statusBadge} ${styles[statusCfg.badgeClass]}`}>
      <span className={styles.statusBadgeIcon}>{statusCfg.icon}</span>
      {statusCfg.label}
    </span>
  );

  const renderInvoiceDetails = () => {
    if (!activeInvoice) return null;
    return (
      <Box className={styles.invoiceDetails}>
        {/* Cabeçalho: título + badge de status + botão cancelar */}
        <div className={styles.invoiceDetailsHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Typography className={styles.invoiceDetailsTitle}>
              Nota Fiscal Ativa
            </Typography>
            {renderStatusBadge()}
          </div>
          {activeInvoice.status === 'issued' && !showCancelConfirm && (
            <Tooltip title="Cancelar NFS-e">
              <IconButton
                size="small"
                onClick={handleCancelRequest}
                sx={{
                  color: 'error.main',
                  border: '1px solid',
                  borderColor: 'error.main',
                  borderRadius: '8px',
                  padding: '4px',
                  '&:hover': {
                    backgroundColor: 'rgba(211, 47, 47, 0.04)',
                  },
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </div>

        {activeInvoice.external_reference && (
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Referência</span>
            <span className={styles.detailValueMono}>{activeInvoice.external_reference}</span>
          </div>
        )}
        {activeInvoice.invoice_number && (
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Número NFS-e</span>
            <span className={styles.detailValue}>{activeInvoice.invoice_number}</span>
          </div>
        )}
        {activeInvoice.status === 'issued' && activeInvoice.issued_at && (
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Data de emissão</span>
            <span className={styles.detailValue}>{formatDateTime(activeInvoice.issued_at)}</span>
          </div>
        )}
        {activeInvoice.amount && (
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Valor</span>
            <span className={styles.detailValue}>
              R$ {parseFloat(activeInvoice.amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </span>
          </div>
        )}
        {activeInvoice.service_description && (
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Descrição</span>
            <span className={styles.detailValue}>{activeInvoice.service_description}</span>
          </div>
        )}
        {activeInvoice.error_message && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {activeInvoice.error_message}
          </Alert>
        )}
        {activeInvoice.status === 'processing' && (
          <Alert severity="info" sx={{ mt: 1 }}>
            A nota está sendo processada. Clique em "Atualizar status" para verificar se foi autorizada.
          </Alert>
        )}
        {downloadErrorMsg && (
          <Alert severity="error" sx={{ mt: 1 }} onClose={() => setDownloadErrorMsg('')}>
            {downloadErrorMsg}
          </Alert>
        )}
        {/* Painel de confirmação de cancelamento */}
        {showCancelConfirm && (
          <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'error.main', borderRadius: 2 }}>
            <Typography variant="subtitle2" color="error" sx={{ mb: 1, fontWeight: 600 }}>
              Confirmar cancelamento da NFS-e
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              Esta ação é irreversível. Informe o motivo do cancelamento (15–255 caracteres):
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={2}
              size="small"
              placeholder="Ex: Nota emitida com dados incorretos do paciente."
              value={cancelJustificativa}
              onChange={(e) => setCancelJustificativa(e.target.value)}
              inputProps={{ maxLength: 255 }}
              helperText={`${cancelJustificativa.length}/255 caracteres (mínimo 15)`}
            />
            <Box sx={{ display: 'flex', gap: 1, mt: 1.5, justifyContent: 'flex-end' }}>
              <CustomButton
                variant="outlined"
                style="tertiary"
                label="Voltar"
                onClick={handleCancelAbort}
                enabled={!canceling}
              />
              <CustomButton
                variant="contained"
                style="error"
                label={canceling ? 'Cancelando...' : 'Confirmar cancelamento'}
                icon={canceling ? <CircularProgress size={16} color="inherit" /> : <CancelOutlinedIcon />}
                onClick={handleCancelConfirm}
                enabled={!canceling && cancelJustificativa.trim().length >= 15}
              />
            </Box>
          </Box>
        )}

        {/* Separador e botões de ação da nota ativa */}
        <Divider sx={{ mt: 1 }} />
        <div className={styles.invoiceActions}>
          {/* Botão de atualizar status (quando em processamento) */}
          {activeInvoice.status === 'processing' && (
            <CustomButton
              variant="outlined"
              style="secondary"
              label={refreshing ? 'Atualizando...' : 'Atualizar status'}
              icon={refreshing ? <CircularProgress size={16} /> : <RefreshOutlinedIcon />}
              onClick={handleRefresh}
              enabled={!refreshing}
            />
          )}

          {/* Botões de download (quando emitida) */}
          {activeInvoice.status === 'issued' && (
            <>
              <Tooltip title={!pdfAvailable ? 'PDF ainda sendo processado...' : ''}>
                <span>
                  <CustomButton
                    variant="outlined"
                    style="secondary"
                    label="PDF"
                    icon={<DownloadOutlinedIcon />}
                    onClick={() => handleDownload('pdf')}
                    enabled={pdfAvailable}
                  />
                </span>
              </Tooltip>
              <Tooltip title={!xmlAvailable ? 'XML ainda sendo processado...' : ''}>
                <span>
                  <CustomButton
                    variant="outlined"
                    style="secondary"
                    label="XML"
                    icon={<DownloadOutlinedIcon />}
                    onClick={() => handleDownload('xml')}
                    enabled={xmlAvailable}
                  />
                </span>
              </Tooltip>
              {/* Botão de enviar por WhatsApp (quando emitida) */}
              <Tooltip title={
                !patientData?.phone
                  ? 'Paciente sem telefone cadastrado'
                  : !pdfAvailable
                  ? 'PDF ainda sendo processado...'
                  : ''
              }>
                <span>
                  <Button
                    variant="outlined"
                    color="success"
                    startIcon={<WhatsAppIcon />}
                    onClick={handleSendWhatsApp}
                    disabled={!patientData?.phone || !pdfAvailable}
                    sx={{ textTransform: 'none', fontWeight: 500 }}
                  >
                    Enviar
                  </Button>
                </span>
              </Tooltip>
            </>
          )}

          {/* Indicador visual de polling — aguardando geração do PDF */}
          {activeInvoice.status === 'issued' && !pdfAvailable && (
            <div className={styles.pdfWaitingIndicator}>
              <CircularProgress size={14} />
              <span>Aguardando geração do PDF... ({pollingAttemptsRef.current}/{MAX_POLLING_ATTEMPTS})</span>
            </div>
          )}
          {/* Situação do envio automático por WhatsApp (vem da fila de envio) */}
          {activeInvoice.status === 'issued' && activeInvoice.auto_send && (
            <div className={styles.autoSendStatus}>
              <WhatsAppIcon sx={{ fontSize: 14 }} />
              <span>{describeFiscalAutoSend(activeInvoice.auto_send)}</span>
            </div>
          )}

        </div>
      </Box>
    );
  };

  return (
    <>
    {/* Modal de revisão de dados antes da emissão */}
    {showReviewModal && (
      <FiscalInvoiceReviewModal
        open={showReviewModal}
        onClose={() => !issuing && setShowReviewModal(false)}
        onConfirm={handleIssue}
        issuing={issuing}
        appointmentData={appointmentData}
        patientData={patientData}
        fiscalSettings={fiscalSettings}
        serviceDescription={serviceDescription}
      />
    )}
    <Dialog
      open={true}
      onClose={onHide}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: '12px',
          padding: '8px',
        },
      }}
    >
      {/* Título */}
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReceiptLongOutlinedIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Nota Fiscal (NFS-e)
          </Typography>
        </Box>
        <IconButton size="small" onClick={onHide}>
          <CloseOutlinedIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        {/* Banner de feature lock por assinatura */}
        {!hasNfsePermission && (
          <FeatureLockBanner
            type="permission"
            featureName="A emissão de NFS-e"
            planName={PROFESSIONAL_PLAN_NAME}
            onUpgradeClick={() => setUpgradeModalOpen(true)}
          />
        )}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Alertas de sucesso/erro */}
            {successMsg && (
              <Alert severity="success" onClose={() => setSuccessMsg('')}>
                {successMsg}
              </Alert>
            )}
            {errorMsg && (
              <Alert severity="error" onClose={() => setErrorMsg('')}>
                {errorMsg}
              </Alert>
            )}

            {/* Avisos de pré-requisitos */}
            {warnings.length > 0 && (
              <Box>
                {warnings.map((w, i) => (
                  <Alert key={i} severity="warning" icon={<WarningAmberOutlinedIcon />} sx={{ mb: 0.5 }}>
                    {w}
                  </Alert>
                ))}
              </Box>
            )}

            {/* Dados do paciente e atendimento */}
            <Box className={styles.summaryBox}>
              <Typography className={styles.summaryTitle}>
                Dados do Atendimento
              </Typography>
              <Typography className={styles.summaryPatientName}>
                {patientData?.name || '—'}
              </Typography>
              <div className={styles.summarySecondaryRow}>
                <span className={styles.summarySecondaryLabel}>CPF/CNPJ</span>
                <span className={styles.summarySecondaryValue}>
                  {patientData?.cpf || patientData?.cnpj || 'Não informado'}
                </span>
              </div>
              {appointmentData?.price && (
                <div className={styles.summarySecondaryRow}>
                  <span className={styles.summarySecondaryLabel}>Valor</span>
                  <span className={styles.summaryValueHighlight}>
                    R$ {parseFloat(appointmentData.price).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              )}
            </Box>

            <Divider />

            {/* Detalhes da nota ativa (quando há nota em andamento) */}
            {activeInvoice && renderInvoiceDetails()}
            {/* Preferência da clínica: envio automático da nota (igual a Preferências > Automação) */}
            {hasNfsePermission && fiscalSettings?.enabled && autoSend.loaded && (
              <Tooltip title={
                autoSend.blocked
                  ? autoSend.blockedMessage
                  : !canEditAutoSend
                  ? 'Apenas proprietários e administradores podem alterar esta preferência'
                  : ''
              }>
                <Box className={styles.autoSendRow}>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        color="secondary"
                        checked={autoSend.enabled}
                        onChange={handleAutoSendToggle}
                        disabled={autoSend.blocked || !canEditAutoSend || autoSendSaving}
                      />
                    }
                    label={<Typography variant="body2">Enviar automaticamente por WhatsApp quando a nota ficar pronta</Typography>}
                  />
                  <Typography variant="caption" className={styles.autoSendHint}>
                    Pelo número próprio da clínica, com o link da nota em um botão. Vale para todas as notas.
                  </Typography>
                </Box>
              </Tooltip>
            )}

            {/* Botão de emitir (quando não há nota ativa — canceled/error permitem reemissão) */}
            {canIssue && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <CustomButton
                  variant="contained"
                  style="secondary"
                  label="Emitir NFS-e"
                  icon={<ReceiptLongOutlinedIcon />}
                  onClick={handleIssueClick}
                  enabled={!issuing && hasNfsePermission}
                />
              </Box>
            )}

            {/* Histórico de notas canceladas/com erro */}
            {invoices.length > 0 && (
              <>
                <Divider />
                <FiscalInvoiceHistory invoices={invoices} />
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <CustomButton
          variant="outlined"
          style="tertiary"
          label="Fechar"
          onClick={onHide}
        />
      </DialogActions>
    </Dialog>

    <PlanUpgradeModal
      isOpen={upgradeModalOpen}
      onClose={() => setUpgradeModalOpen(false)}
      featureName="A emissão de NFS-e"
      planName={PROFESSIONAL_PLAN_NAME}
      type="permission"
    />
    </>
  );
}

export default FiscalInvoicePopup;
